import { ReactNode } from 'react'

import clsx from 'clsx'

import { ChildProps } from '@/common/util/props'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import IconButton from '@/components/view/atoms/button/IconButton'
import Icon, { IconType } from '@/components/view/atoms/icon/Icon'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import TooltipWrapper from '@/components/view/utils/tooltip/TooltipWrapper'

import { TextBoxSize } from '../textfield/TextField'

const FormLabelInner = ({
  children,
  id,
  badge,
  behindNode,
  rightNode,
  badgePositionBehind = false,
  required,
  fixWidth = 128,
  help,
  size = TextBoxSize.FIT,
  color = TextColor.PRIMARY,
  indent = false,
  weight = 'bold'
}: Props) => {
  const fixWidthClass =
    fixWidth == 80
      ? 'w-[80px] min-w-[80px]'
      : fixWidth == 128
        ? 'w-[128px] min-w-[128px]'
        : fixWidth == 176
          ? 'w-[176px] min-w-[176px]'
          : ''

  const widthClass =
    size == TextBoxSize.X_SMALL
      ? 'w-14 max-w-14'
      : size == TextBoxSize.SMALL
        ? 'w-22 max-w-22'
        : size == TextBoxSize.MEDIUM
          ? 'w-46 max-w-46'
          : size == TextBoxSize.LARGE
            ? 'w-[20rem] max-w-[20rem]'
            : size == TextBoxSize.FIT
              ? 'w-fit'
              : size == TextBoxSize.REMNANT
                ? 'flex-1'
                : 'w-full'

  return (
    <div
      id={id}
      className={clsx(
        'flex h-[26px] items-center',
        fixWidth ? fixWidthClass : widthClass,
        rightNode && 'w-full',
        badgePositionBehind ? 'gap-x-1' : 'gap-x-2',
        indent && 'pl-2'
      )}
    >
      <div className='flex items-center gap-x-2'>
        {badge && !badgePositionBehind && <>{badge}</>}
        <Text tag={'span'} size={12} weight={weight} color={color}>
          {children}
        </Text>
        {required && (
          <Badge badgeType={BadgeType.REQUIRED} size={10} fixed>
            必須
          </Badge>
        )}
        {help && (
          <TooltipWrapper text={help} className='flex'>
            <IconButton type={IconType.HELP} size={14} line />
          </TooltipWrapper>
        )}
      </div>
      {badge && badgePositionBehind && <>{badge}</>}
      {behindNode && <div className='flex max-h-[15px] items-center'>{behindNode}</div>}
      {rightNode && <div className='ml-auto flex max-h-[15px] items-center'>{rightNode}</div>}
    </div>
  )
}

interface Props extends ChildProps {
  id?: string
  badge?: ReactNode
  behindNode?: ReactNode
  rightNode?: ReactNode
  badgePositionBehind?: boolean
  required?: boolean
  fixWidth?: 80 | 128 | 176 | null
  help?: string
  subText?: string
  size?: TextBoxSize
  color?: TextColor
  indent?: boolean
  weight?: 'normal' | 'bold'
}

const FormLabel = (props: Props) => {
  return props.subText ? (
    <div className='flex flex-col gap-1'>
      <FormLabelInner {...props} />
      {props.subText && (
        <div className='text-sumi-600 text-size-10 w-[128px] leading-120'>{props.subText}</div>
      )}
    </div>
  ) : (
    <FormLabelInner {...props} />
  )
}

export default FormLabel
